/**
 * @file ConsentTextsEditPage.tsx
 * @description «Платформа → Согласие на cookie → Новая редакция»
 * (phase19.16, Р3г, v0.19.62; uiux-r3g-consent-texts §3, §5). Адрес
 * `/platform/consent-texts/new?kind=BANNER|NOTICE_RISK[&base=N][&draft=1]`:
 * вид, основа («Взять за основу» ред. N) и черновик браузера («Продолжить»).
 *
 * Страница = загрузка начала правки (`useConsentTextsSource`) и её
 * состояния; сама форма — `ConsentTextsEditor`. Право
 * `PLATFORM_MANAGE_PERSONAL_DATA` проверяет маршрут.
 */

import { FileWarning } from "lucide-react";
import { useSearchParams } from "react-router";

import { ConsentTextsEditor } from "../../components/platform/consent-texts/ConsentTextsEditor";
import { CabinetPageHeader } from "../../components/ui/CabinetPageHeader";
import { EmptyState } from "../../components/ui/EmptyState";
import { ListError } from "../../components/ui/ListStates";
import { Skeleton } from "../../components/ui/skeleton";
import { useConsentTextsSource } from "../../hooks/platform/useConsentTextsSource";
import { kindFromParam } from "../../lib/platform/consent-texts-fields";

function baseFromParam(value: string | null): number | null {
  const n = Number(value);
  return value !== null && Number.isInteger(n) && n >= 1 ? n : null;
}

export function ConsentTextsEditPage() {
  const [params] = useSearchParams();
  const kind = kindFromParam(params.get("kind"));
  const source = useConsentTextsSource({ kind, baseNumber: baseFromParam(params.get("base")), fromDraft: params.get("draft") === "1" });

  if (source.status === "ready") {
    const { current, start } = source;
    return (
      <ConsentTextsEditor
        key={`${kind}-${current.number}-${start.base.number}-${start.draftSavedAt ?? ""}`}
        kind={kind}
        current={current}
        start={start}
        onReload={source.reload}
      />
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <CabinetPageHeader title="Новая редакция" back={{ to: "/platform/consent-texts", label: "К текстам согласия" }} />
      {source.status === "loading" ? (
        <div aria-busy="true" aria-label="Загрузка формы" className="grid gap-6 xl:grid-cols-[minmax(0,40rem)_minmax(25rem,1fr)]">
          <div className="flex flex-col gap-4">
            {[0, 1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-20 w-full" />
            ))}
          </div>
          <Skeleton className="h-64 w-full" />
        </div>
      ) : null}
      {source.status === "error" ? <ListError message={source.message} onRetry={source.reload} /> : null}
      {source.status === "empty" ? (
        <EmptyState
          icon={FileWarning}
          title="Редакций нет — исходная редакция 1 не загружена"
          description="Сайты показывают встроенный текст редакции 1. Сообщите разработчику."
        />
      ) : null}
    </div>
  );
}
